import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import type { ItemDto, ItemStatus, ItemType } from '../api/types';
import { AddItemDialog } from '../components/AddItemDialog';
import { ItemCard } from '../components/cards';
import { IconLibrary, IconPlus, IconSearch } from '../components/icons';
import { CardSkeletons, EmptyState, ErrorState, TextInput } from '../components/ui';
import { useDebounced } from '../hooks/useAsync';
import { isItemStatus, statusOptions, TYPE_ORDER, typeOptions } from '../lib/format';
import { t } from '../i18n';
import { useToast } from '../components/Toast';

const TYPE_VALUES = new Set<string>(TYPE_ORDER);
type StatusFilter = ItemStatus | 'all';

export function LibraryPage() {
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState<ItemDto[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  // The filter lives in the URL, so opening a card and coming back keeps the tab the user picked.
  const rawType = params.get('type');
  const type: ItemType | 'all' = rawType !== null && TYPE_VALUES.has(rawType) ? (rawType as ItemType) : 'all';
  const rawStatus = params.get('status');
  const status: StatusFilter = rawStatus !== null && isItemStatus(rawStatus) ? rawStatus : 'all';
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounced(query);
  const requestId = useRef(0);
  const addFlag = params.get('add') === '1';
  const [addOpen, setAddOpen] = useState(addFlag);

  useEffect(() => setAddOpen(addFlag), [addFlag]);

  const load = useCallback(
    async (mode: 'reset' | 'more') => {
      const ticket = ++requestId.current;
      setLoading(true);
      setError(null);
      try {
        const page = await api.items.list({
          limit: 24,
          ...(type === 'all' ? {} : { type }),
          ...(status === 'all' ? {} : { status }),
          ...(debouncedQuery.trim() ? { q: debouncedQuery.trim() } : {}),
          ...(mode === 'more' && cursor ? { cursor } : {}),
        });
        if (ticket !== requestId.current) return;
        setItems((current) => (mode === 'more' ? [...current, ...page.items] : page.items));
        setCursor(page.nextCursor);
      } catch (caught) {
        if (ticket !== requestId.current) return;
        setError(caught);
      } finally {
        if (ticket === requestId.current) setLoading(false);
      }
    },
    [type, status, debouncedQuery, cursor],
  );

  useEffect(() => {
    void load('reset');
    // Filters changed: always re-read from the first page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, status, debouncedQuery]);

  function setType(next: ItemType | 'all') {
    const buffer = new URLSearchParams(params);
    if (next === 'all') {
      // Without a type there is no wording for a status, so an invisible filter would be worst.
      buffer.delete('type');
      buffer.delete('status');
    } else buffer.set('type', next);
    setParams(buffer, { replace: true });
  }

  function setStatus(next: StatusFilter) {
    const buffer = new URLSearchParams(params);
    if (next === 'all') buffer.delete('status');
    else buffer.set('status', next);
    setParams(buffer, { replace: true });
  }

  function resetFilters() {
    const buffer = new URLSearchParams(params);
    buffer.delete('type');
    buffer.delete('status');
    setParams(buffer, { replace: true });
    setQuery('');
  }

  function closeAdd() {
    setAddOpen(false);
    if (addFlag) {
      params.delete('add');
      setParams(params, { replace: true });
    }
  }

  const filtered = Boolean(debouncedQuery) || type !== 'all' || status !== 'all';

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>{t('nav.library')}</h1>
          <p className="page-head__sub">
            {loading && items.length === 0 ? t('ui.loadingShort') : t('library.saved', { n: items.length })}
          </p>
        </div>
        <button type="button" className="btn btn--primary library-add" onClick={() => setAddOpen(true)}>
          <IconPlus size={18} /> {t('nav.addLink')}
        </button>
      </div>

      <div className="toolbar library-toolbar">
        <div className="search-field">
          <span className="search-field__icon">
            <IconSearch size={18} />
          </span>
          <TextInput
            type="search"
            aria-label={t('library.searchAria')}
            placeholder={t('library.searchPlaceholder')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <div className="toolbar__filters" role="group" aria-label={t('library.filterType')}>
          <button type="button" className="chip" aria-pressed={type === 'all'} onClick={() => setType('all')}>
            {t('library.all')}
          </button>
          {typeOptions().map(({ value, label }) => (
            <button key={value} type="button" className="chip" aria-pressed={type === value} onClick={() => setType(value)}>
              {label}
            </button>
          ))}
        </div>
        {type === 'all' ? null : (
          <div className="toolbar__filters" role="group" aria-label={t('library.filterStatus')}>
            <button type="button" className="chip" aria-pressed={status === 'all'} onClick={() => setStatus('all')}>
              {t('library.anyStatus')}
            </button>
            {statusOptions(type).map(({ value, label }) => (
              <button
                key={value}
                type="button"
                className="chip"
                aria-pressed={status === value}
                onClick={() => setStatus(value)}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {error ? (
        <ErrorState error={error} onRetry={() => void load('reset')} />
      ) : loading && items.length === 0 ? (
        <CardSkeletons />
      ) : items.length === 0 ? (
        <EmptyState
          icon={<IconLibrary />}
          title={filtered ? t('library.nothingFound') : t('library.isEmpty')}
          body={
            filtered
              ? t('library.nothingFoundHint')
              : t('library.isEmptyHint')
          }
          action={
            filtered ? (
              <button type="button" className="btn btn--outline" onClick={resetFilters}>
                {t('library.resetFilters')}
              </button>
            ) : (
              <button type="button" className="btn btn--primary" onClick={() => setAddOpen(true)}>
                <IconPlus size={18} /> {t('library.addFirst')}
              </button>
            )
          }
        />
      ) : (
        <>
          <div className="grid">
            {items.map((item) => (
              <ItemCard key={item.id} item={item} to={`/app/items/${item.id}`} />
            ))}
          </div>
          <div className="row" style={{ justifyContent: 'center', marginTop: 'var(--sp-6)' }}>
            {cursor ? (
              <button type="button" className="btn btn--outline" disabled={loading} onClick={() => void load('more')}>
                {loading ? t('ui.loadingShort') : t('library.showMore')}
              </button>
            ) : (
              <span className="tiny muted">{t('library.endOfList')}</span>
            )}
          </div>
        </>
      )}

      <AddItemDialog
        open={addOpen}
        onClose={closeAdd}
        onSaved={() => {
          closeAdd();
          if (filtered) {
            toast.push(t('library.filterCleared'));
            resetFilters();
          } else {
            void load('reset');
          }
        }}
      />
    </div>
  );
}
